import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, OnInit, inject, Input, ViewEncapsulation } from '@angular/core';
import { IonBadge, IonContent, IonFooter, IonHeader, ModalController } from '@ionic/angular/standalone';
import { ButtonComponent, IconComponent, SelectComponent, SelectOption, SensorGroup, SensorsFiltersPage, CheckboxComponent, MultiSelectComponent } from '@net3-multiplatform-client/libs';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SensorGroupType } from 'libs/domain/sensor/sensor.enums';
import { SelectOptionMultiple } from '../../../../shared';

export type SortDirection = 'ASC' | 'DESC';

/**
 * Modal de filtres per a la llista de sensors.
 * Es crea via ModalController.create() des de Sensors.Component
 * Al confirmar retorna un SensorsFiltersPage
 */

@Component({
  selector: 'lib-sensor-list-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonHeader, 
    IonContent, 
    IonFooter, 
    IonBadge, 
    SelectComponent,
    IconComponent,
    ButtonComponent,
    CheckboxComponent,
    MultiSelectComponent
],
  templateUrl: './sensor-list-filter.html',
  styleUrl: './sensor-list-filter.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SensorListFilter implements OnInit {
  private fb = inject(ModalController);
  private modalCtrl = inject(ModalController);

  @Input() filters: SensorsFiltersPage = {};
  @Input() groupOptions: SelectOptionMultiple[] = [];
  @Input() sectorOptions: SelectOptionMultiple[] = [];

  groupsControl = new FormControl<string[]>([], { nonNullable: true });
  sectorsControl = new FormControl<string[]>([], { nonNullable: true });

  form!: FormGroup;

  orders: SelectOption[] = [
    { label: 'Id', value: 'ID:ASC' },
    { label: 'Nombre (A-Z)', value: 'NAME:ASC' },
    { label: 'Nombre (Z-A)', value: 'NAME:DESC' },
    { label: 'Estado', value: 'STATE:DESC' },
  ];

  ngOnInit(): void {
    const initialOrder = this.filters.sortBy
      ? `${this.filters.sortBy.toUpperCase()}:${this.filters.sortDirection ?? 'ASC'}`
      : 'NAME:ASC';

    const allGroups = this.filters.groups ?? [];
    this.sectorsControl.setValue(
      allGroups.filter(g => g.type === SensorGroupType.SECTOR).map(g => g.id.toString())
    );
    this.groupsControl.setValue(
      allGroups.filter(g => g.type === SensorGroupType.VISUALIZATION_GROUP).map(g => g.id.toString())
    );

    this.form = new FormGroup({
      groups: this.groupsControl,
      sectors: this.sectorsControl,
      hideNoGroupSensors: new FormControl(this.filters.hideNoGroupSensors ?? false),
      hasError: new FormControl(this.filters.hasError ?? false),
      order: new FormControl(initialOrder),
    });
  }

  get orderValue(): string { return this.form.get('order')?.value ?? 'NAME:ASC'; }

  get hasActiveFilters(): boolean {
    return this.groupsControl.value.length > 0
      || this.sectorsControl.value.length > 0
      || !!this.form.get('hideNoGroupSensors')?.value
      || !!this.form.get('hasError')?.value;
  }

  getGroupLabel(id: string): string {
    return this.groupOptions.find(o => o.value === id)?.label ?? id;
  }

  getSectorLabel(id: string): string {
    return this.sectorOptions.find(o => o.value === id)?.label ?? id;
  }

  removeGroup(id: string): void {
    this.groupsControl.setValue(this.groupsControl.value.filter(v => v !== id));
    this.form.markAsDirty();
  }

  removeSector(id: string): void {
    this.sectorsControl.setValue(this.sectorsControl.value.filter(v => v !== id));
    this.form.markAsDirty();
  }

  toggleNoGroupSensors(): void {
    this.form.get('hideNoGroupSensors')?.setValue(!this.form.get('hideNoGroupSensors')?.value);
    this.form.markAsDirty();
  }

  toggleHasError(): void {
    this.form.get('hasError')?.setValue(!this.form.get('hasError')?.value);
    this.form.markAsDirty();
  }

  clearFilters(): void {
    this.groupsControl.setValue([]);
    this.sectorsControl.setValue([]);
    this.form.get('hideNoGroupSensors')?.setValue(false);
    this.form.get('hasError')?.setValue(false);
    this.form.markAsDirty();
  }

  confirm(): void {
    const { hideNoGroupSensors, hasError, order } = this.form.value;
    const [sortBy, sortDirection] = (order as string).split(':');

    const selectedSectors: SensorGroup[] = this.sectorsControl.value.map((id: string) => ({
      id:   parseInt(id),
      name: this.sectorOptions.find(o => o.value === id)?.label ?? '',
      type: SensorGroupType.SECTOR,
    }));

    const selectedGroups: SensorGroup[] = this.groupsControl.value.map((id: string) => ({
      id:   parseInt(id),
      name: this.groupOptions.find(o => o.value === id)?.label ?? '',
      type: SensorGroupType.VISUALIZATION_GROUP,
    }));

    this.modalCtrl.dismiss(
      {
        ...this.filters,
        groups:  [...selectedSectors, ...selectedGroups],
        hideNoGroupSensors: hideNoGroupSensors,
        hasError,
        sortBy: sortBy,
        sortDirection: sortDirection as SortDirection,
      } satisfies SensorsFiltersPage,
      'confirm'
    );
  }

  cancel(): void {
    this.modalCtrl.dismiss(null, 'cancel');
  }
}